import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import classNames from 'classnames';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';

import {
    deliveryReturnsTitle,
    deliveryTitle,
    methodNoteFreeShipping,
} from '../messages/pdpDetailsMessages.jsx';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping.tsx';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy.tsx';
import { PdpSharedBuyerProtectionBox } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionBox.tsx';
import { PdpSharedBuyerProtectionAuctions } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionAuctions.tsx';
import { PdpRespDetail } from './PdpRespDetail.tsx';
import { useSbSelector } from '../../reducers/useSbSelector.ts';

import styles from './PdpRespDetails.scss';

function PdpRespShippingReturnsComponent({
    currency,
    item,
    sku,
    user,
    itemQuantity,
    isCondensedTab,
}) {
    const { seller, isListingMigrated, isAuctionEnabled } = item;
    const { userId, itemId, afterDisplay } = useSbSelector(
        state => state.relayVariables?.variables
    );

    if (!seller?.isAccessible || isListingMigrated) {
        return null;
    }

    const returnPolicySectionHeader = (
        <span
            dangerouslySetInnerHTML={{
                __html: item?.returnPolicy?.buyerCopy?.returnPolicySectionHeader?.htmlCopy || '',
            }}
        />
    );

    const preLinkQuoteDetails = <>{methodNoteFreeShipping} </>;

    return (
        <div
            className={classNames(styles.wrapper, styles.shippingModule, {
                [styles.noMargin]: isCondensedTab,
            })}
            data-tn="pdp-details-shipping"
        >
            <HeadingLevel>
                {Heading => (
                    <>
                        <GridRow>
                            <GridCol desktop={4} mobile={6}>
                                {!isCondensedTab && (
                                    <Heading className={styles.header}>
                                        {deliveryReturnsTitle}
                                    </Heading>
                                )}
                                <ul className={styles.shippingReturnsRow}>
                                    {/* Shipping */}
                                    <PdpRespDetail
                                        title={deliveryTitle}
                                        dataTn="shipping"
                                        printHide
                                    >
                                        <PdpSharedDetailShipping
                                            currency={currency}
                                            userId={userId}
                                            itemId={itemId}
                                            isClient={afterDisplay}
                                            item={item}
                                            user={user}
                                            preLinkQuoteDetails={preLinkQuoteDetails}
                                            displayInline
                                            itemQuantity={itemQuantity}
                                        />
                                    </PdpRespDetail>
                                    {/* Return Policy */}
                                    <PdpRespDetail
                                        title={returnPolicySectionHeader}
                                        dataTn="return-policy"
                                    >
                                        <PdpSharedDetailReturnPolicy
                                            item={item}
                                            user={user}
                                            sku={sku}
                                            itemQuantity={itemQuantity}
                                            userId={userId}
                                            itemId={itemId}
                                            isClient={afterDisplay}
                                        />
                                    </PdpRespDetail>
                                </ul>
                            </GridCol>
                            <GridCol desktop={4} mobile={12}>
                                <div
                                    className={classNames(
                                        styles.printHide,
                                        styles.buyerProtectionRow,
                                        {
                                            [styles.marginTop]: !isCondensedTab,
                                        }
                                    )}
                                >
                                    {isAuctionEnabled && <PdpSharedBuyerProtectionAuctions />}

                                    <PdpSharedBuyerProtectionBox />
                                </div>
                            </GridCol>
                        </GridRow>
                    </>
                )}
            </HeadingLevel>
        </div>
    );
}

PdpRespShippingReturnsComponent.propTypes = {
    currency: PropTypes.string,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    sku: PropTypes.object,
    itemQuantity: PropTypes.number,
    isCondensedTab: PropTypes.bool,
};

export const PdpRespShippingReturns = createFragmentContainer(PdpRespShippingReturnsComponent, {
    user: graphql`
        fragment PdpRespShippingReturns_user on User {
            ...PdpSharedDetailShipping_user
            ...PdpSharedDetailReturnPolicy_user
        }
    `,
    item: graphql`
        fragment PdpRespShippingReturns_item on Item {
            ...PdpSharedDetailReturnPolicy_item
            ...PdpSharedDetailShipping_item
            returnPolicy(userId: $userId) {
                buyerCopy {
                    returnPolicySectionHeader {
                        htmlCopy
                    }
                }
            }
            seller {
                isAccessible
            }
            isAuctionEnabled
            isListingMigrated
        }
    `,
    sku: graphql`
        fragment PdpRespShippingReturns_sku on ItemSkuType {
            ...PdpSharedDetailReturnPolicy_sku
        }
    `,
});
